import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { getAllCategoryConfigs, getCategoryConfig } from '../config/categoryConfigs';
// import { getCategoryDataSource } from '../utils/categoryUtils'; // For future use

// Action types
const CATEGORY_ACTIONS = {
  SET_LOADING: 'SET_LOADING',
  SET_CATEGORIES: 'SET_CATEGORIES',
  SET_CURRENT_CATEGORY: 'SET_CURRENT_CATEGORY',
  SET_SERVICES: 'SET_SERVICES',
  SET_FILTERS: 'SET_FILTERS',
  SET_SEARCH_QUERY: 'SET_SEARCH_QUERY',
  SET_ERROR: 'SET_ERROR',
  CLEAR_ERROR: 'CLEAR_ERROR',
  TOGGLE_FAVORITE: 'TOGGLE_FAVORITE',
  SET_VIEW_MODE: 'SET_VIEW_MODE'
};

// Initial state
const initialState = {
  // Categories
  categories: [],
  currentCategory: null,
  
  // Services
  services: [],
  filteredServices: [],
  isLoading: false,
  
  // Filters and search
  filters: {
    priceRange: { min: 0, max: 5000 },
    duration: 'all',
    rating: 0,
    features: [],
    sortBy: 'popularity'
  },
  searchQuery: '',
  
  // UI state
  viewMode: 'grid', // 'grid' or 'list'
  favorites: [],
  
  // Error handling
  error: null
};

// Reducer
const categoryReducer = (state, action) => {
  switch (action.type) {
    case CATEGORY_ACTIONS.SET_LOADING:
      return { ...state, isLoading: action.payload };
      
    case CATEGORY_ACTIONS.SET_CATEGORIES:
      return { ...state, categories: action.payload };
      
    case CATEGORY_ACTIONS.SET_CURRENT_CATEGORY:
      return { ...state, currentCategory: action.payload };
      
    case CATEGORY_ACTIONS.SET_SERVICES:
      return { 
        ...state, 
        services: action.payload,
        filteredServices: action.payload 
      };
      
    case CATEGORY_ACTIONS.SET_FILTERS:
      const newFilters = { ...state.filters, ...action.payload };
      return {
        ...state,
        filters: newFilters,
        filteredServices: applyFilters(state.services, newFilters, state.searchQuery)
      };
      
    case CATEGORY_ACTIONS.SET_SEARCH_QUERY:
      return {
        ...state,
        searchQuery: action.payload,
        filteredServices: applyFilters(state.services, state.filters, action.payload)
      };
      
    case CATEGORY_ACTIONS.SET_ERROR:
      return { ...state, error: action.payload, isLoading: false };
      
    case CATEGORY_ACTIONS.CLEAR_ERROR:
      return { ...state, error: null };
      
    case CATEGORY_ACTIONS.TOGGLE_FAVORITE:
      const serviceId = action.payload;
      const newFavorites = state.favorites.includes(serviceId)
        ? state.favorites.filter(id => id !== serviceId)
        : [...state.favorites, serviceId];
      return { ...state, favorites: newFavorites };
      
    case CATEGORY_ACTIONS.SET_VIEW_MODE:
      return { ...state, viewMode: action.payload };
      
    default:
      return state;
  }
};

// Filter application logic
const applyFilters = (services, filters, searchQuery) => {
  let filtered = [...services];

  // Search filter
  if (searchQuery) {
    const query = searchQuery.toLowerCase();
    filtered = filtered.filter(service =>
      service.title.toLowerCase().includes(query) ||
      service.description.toLowerCase().includes(query)
    );
  }

  // Price range filter
  if (filters.priceRange) {
    filtered = filtered.filter(service =>
      service.price >= filters.priceRange.min &&
      service.price <= filters.priceRange.max
    );
  }

  // Duration filter
  if (filters.duration !== 'all') {
    filtered = filtered.filter(service => {
      const duration = service.duration.toLowerCase();
      switch (filters.duration) {
        case '1hr': return duration.includes('1 hour');
        case '2hr': return duration.includes('1-2') || duration.includes('2 hours');
        case '3hr': return duration.includes('2-3') || duration.includes('3 hours');
        case '4hr+': return duration.includes('3+') || duration.includes('4+');
        default: return true;
      }
    });
  }

  // Rating filter
  if (filters.rating > 0) {
    filtered = filtered.filter(service => service.rating >= filters.rating);
  }

  // Features filter
  if (filters.features.length > 0) {
    filtered = filtered.filter(service =>
      filters.features.every(feature =>
        service.features && service.features.includes(feature)
      )
    );
  }

  // Sorting
  switch (filters.sortBy) {
    case 'price_low':
      filtered.sort((a, b) => a.price - b.price);
      break;
    case 'price_high':
      filtered.sort((a, b) => b.price - a.price);
      break;
    case 'rating':
      filtered.sort((a, b) => b.rating - a.rating);
      break;
    case 'duration':
      filtered.sort((a, b) => a.duration.localeCompare(b.duration));
      break;
    case 'popularity':
    default:
      filtered.sort((a, b) => (b.reviewCount || 0) - (a.reviewCount || 0));
      break;
  }

  return filtered;
};

// Context
const CategoryContext = createContext();

// Provider component
export const CategoryProvider = ({ children }) => {
  const [state, dispatch] = useReducer(categoryReducer, initialState);

  // Initialize categories
  useEffect(() => {
    try {
      const categories = getAllCategoryConfigs();
      dispatch({ type: CATEGORY_ACTIONS.SET_CATEGORIES, payload: categories });
    } catch (error) {
      dispatch({ type: CATEGORY_ACTIONS.SET_ERROR, payload: error.message });
    }
  }, []);

  // Load favorites from localStorage
  useEffect(() => {
    try {
      const savedFavorites = localStorage.getItem('categoryFavorites');
      if (savedFavorites) {
        const favorites = JSON.parse(savedFavorites);
        dispatch({ type: CATEGORY_ACTIONS.TOGGLE_FAVORITE, payload: favorites });
      }
    } catch (error) {
      console.warn('Failed to load favorites from localStorage:', error);
    }
  }, []);

  // Save favorites to localStorage
  useEffect(() => {
    try {
      localStorage.setItem('categoryFavorites', JSON.stringify(state.favorites));
    } catch (error) {
      console.warn('Failed to save favorites to localStorage:', error);
    }
  }, [state.favorites]);

  // Action creators
  const actions = {
    setLoading: (loading) =>
      dispatch({ type: CATEGORY_ACTIONS.SET_LOADING, payload: loading }),

    setCurrentCategory: (categoryType) => {
      try {
        const config = getCategoryConfig(categoryType);
        dispatch({ type: CATEGORY_ACTIONS.SET_CURRENT_CATEGORY, payload: config });
        return config;
      } catch (error) {
        dispatch({ type: CATEGORY_ACTIONS.SET_ERROR, payload: error.message });
        return null;
      }
    },

    loadCategoryServices: async (categoryType) => {
      try {
        dispatch({ type: CATEGORY_ACTIONS.SET_LOADING, payload: true });
        dispatch({ type: CATEGORY_ACTIONS.CLEAR_ERROR });

        const config = getCategoryConfig(categoryType);
        // const dataSource = getCategoryDataSource(categoryType); // For future API integration

        // Mock API call - replace with actual service
        await new Promise(resolve => setTimeout(resolve, 500));

        // Mock services data - replace with actual API call
        const mockServices = Array.from({ length: 6 }, (_, index) => ({
          id: `${config.name}-${index + 1}`,
          title: `${config.displayName} Service ${index + 1}`,
          description: `Professional ${config.displayName.toLowerCase()} service with experienced pandits`,
          price: 500 + (index * 200),
          duration: ['1 hour', '1-2 hours', '2-3 hours', '3+ hours'][index % 4],
          rating: 4.2 + (index * 0.1),
          reviewCount: 50 + (index * 20),
          image: config.image,
          features: config.features.slice(0, 2 + (index % 3)),
          category: config.name
        }));

        dispatch({ type: CATEGORY_ACTIONS.SET_SERVICES, payload: mockServices });
      } catch (error) {
        dispatch({ type: CATEGORY_ACTIONS.SET_ERROR, payload: error.message });
      } finally {
        dispatch({ type: CATEGORY_ACTIONS.SET_LOADING, payload: false });
      }
    },

    updateFilters: (newFilters) =>
      dispatch({ type: CATEGORY_ACTIONS.SET_FILTERS, payload: newFilters }),

    setSearchQuery: (query) =>
      dispatch({ type: CATEGORY_ACTIONS.SET_SEARCH_QUERY, payload: query }),

    toggleFavorite: (serviceId) =>
      dispatch({ type: CATEGORY_ACTIONS.TOGGLE_FAVORITE, payload: serviceId }),

    setViewMode: (mode) =>
      dispatch({ type: CATEGORY_ACTIONS.SET_VIEW_MODE, payload: mode }),

    clearError: () =>
      dispatch({ type: CATEGORY_ACTIONS.CLEAR_ERROR })
  };

  const value = {
    ...state,
    actions
  };

  return (
    <CategoryContext.Provider value={value}>
      {children}
    </CategoryContext.Provider>
  );
};

// Custom hook to use category context
export const useCategoryContext = () => {
  const context = useContext(CategoryContext);
  if (!context) {
    throw new Error('useCategoryContext must be used within a CategoryProvider');
  }
  return context;
};

// Export action types for external use
export { CATEGORY_ACTIONS };
